import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Sidebar from '@/components/feature/Sidebar';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import { getCategoryBySlug } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);
  const article = getArticleById(articleId);
  const category = article ? getCategoryBySlug(article.categoryId) : null;
  const relatedArticles = article ? getRelatedArticles(article.id, article.categoryId) : [];

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16">
          <div className="container-custom py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-950 mb-2 font-heading">文章不存在</h1>
            <p className="text-foreground-500 mb-6">您访问的文章可能已被删除或链接失效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              <i className="ri-home-4-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const jsonLdArticle = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    url: `https://haijiao.com/article/${article.id}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '海角论坛',
    },
  };

  const jsonLdBreadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://haijiao.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.categoryName,
        item: `https://haijiao.com/category/${article.categoryId}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLdArticle)}</script>
      <script type="application/ld+json">{JSON.stringify(jsonLdBreadcrumb)}</script>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="container-custom py-5">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.categoryId}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <div className="container-custom py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            <article className="flex-1 min-w-0">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  {article.isPinned && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                      <i className="ri-pushpin-line text-[10px]"></i>置顶
                    </span>
                  )}
                  {article.isHot && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">
                      <i className="ri-fire-line text-[10px]"></i>热门
                    </span>
                  )}
                  <Link
                    to={`/category/${article.categoryId}`}
                    className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full hover:bg-secondary-200 transition-colors"
                  >
                    {article.categoryName}
                  </Link>
                </div>

                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 font-heading leading-tight">
                  {article.title}
                </h1>

                <div className="flex items-center gap-4 pb-5 mb-6 border-b border-background-200/70 flex-wrap">
                  <div className="flex items-center gap-2">
                    <img
                      src={article.authorAvatar}
                      alt={article.author}
                      className="w-8 h-8 rounded-full object-cover"
                      width="32"
                      height="32"
                    />
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{article.author}</div>
                      <div className="text-xs text-foreground-500">{article.publishDate}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-foreground-500 ml-auto">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>{article.readCount.toLocaleString()} 阅读
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-chat-3-line"></i>{article.commentCount} 评论
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-heart-line"></i>{article.likes} 点赞
                    </span>
                  </div>
                </div>

                <div
                  className="prose prose-lg max-w-none
                    prose-headings:text-foreground-950 prose-headings:font-heading
                    prose-h2:text-xl prose-h2:font-bold prose-h2:mt-8 prose-h2:mb-4
                    prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                    prose-strong:text-foreground-900
                    prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700"
                  dangerouslySetInnerHTML={{ __html: article.contentHtml }}
                />

                <div className="mt-8 pt-6 border-t border-background-200/70">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-foreground-500">标签：</span>
                    {article.tags.map((tag) => (
                      <span key={tag} className="text-xs text-foreground-600 bg-background-100 px-2.5 py-1 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {relatedArticles.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-bold text-foreground-950 mb-4 font-heading flex items-center gap-2">
                    <i className="ri-links-line text-primary-500"></i>相关文章
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.id}
                        to={`/article/${ra.id}`}
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        className="bg-background-50 border border-background-200/70 rounded-lg p-4 hover:border-primary-300 transition-all group"
                      >
                        <h4 className="text-sm font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                          {ra.title}
                        </h4>
                        <p className="text-xs text-foreground-500 line-clamp-1">{ra.excerpt}</p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-foreground-500">
                          <span>{ra.author}</span>
                          <span>·</span>
                          <span>{ra.readCount.toLocaleString()} 阅读</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </article>

            <div className="w-full lg:w-[320px] flex-shrink-0">
              <Sidebar />
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}